import { useQueryClient } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import type {
  AccessToken,
  Features,
  Model,
  PlaygroundPermissions,
  Prediction,
  Version,
} from "../../types";
import { SvixCustomerContext } from "../svix/context";
import { PlaygroundContext, type PlaygroundContextValue } from "./context";
import { queryKeys } from "./hooks";
import { PredictionDetail } from "./prediction-detail";
import { getElementVisibility, getRenderMode } from "./util";

export default function PredictionDetailView({
  area,
  features,
  isAuthenticated,
  isViewingWithSuperpowers,
  model,
  permissions,
  prediction,
  requiresCredit,
  showHeader,
  svixCustomerContext,
  token,
  usesVersionlessApi,
  version,
}: {
  area: "dashboard" | "explore";
  features: Features;
  isAuthenticated: boolean;
  isViewingWithSuperpowers: boolean;
  model: Model | null;
  permissions: PlaygroundPermissions;
  prediction: Prediction;
  requiresCredit: boolean;
  showHeader?: boolean;
  svixCustomerContext: SvixCustomerContext | null;
  token?: AccessToken;
} & (
  | {
      usesVersionlessApi: true;
      version: Version | null;
    }
  | {
      usesVersionlessApi: false;
      version: Version;
    }
)) {
  const queryClient = useQueryClient();
  queryClient.setQueryData<Prediction>(
    queryKeys.predictions.uuid(prediction.id),
    prediction
  );

  const context: PlaygroundContextValue = {
    elementVisibility: getElementVisibility({
      permissions,
      deployment: undefined,
    }),
    features: features,
    hideAdvancedInputs: false,
    model,
    modelInputSettings: { hidden: [] },
    modelStatus: null,
    isAuthenticated: isAuthenticated,
    permissions: permissions,
    requiresCredit,
    renderMode: getRenderMode({ features }),
    token: token ?? null,
    ...(usesVersionlessApi
      ? {
          usesVersionlessApi: true,
          version,
        }
      : {
          usesVersionlessApi: false,
          version,
        }),
  };

  return (
    <PlaygroundContext.Provider value={context}>
      <SvixCustomerContext.Provider value={svixCustomerContext}>
        <PredictionDetail
          area={area}
          isViewingWithSuperpowers={isViewingWithSuperpowers}
          prediction={prediction}
          showHeader={showHeader}
        />
      </SvixCustomerContext.Provider>
      <Toaster />
    </PlaygroundContext.Provider>
  );
}
